/* eslint-disable import/no-relative-packages */
import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Hydrate } from "react-query/hydration";
import { ReactQueryDevtools } from "react-query/devtools";
import { find } from "lodash";
import { useRouter } from "next/router";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { usePrevious, encodeUri } from "@gonoodle/gn-universe-utils";
import { SSRProvider, OverlayProvider, Toast } from "@gonoodle/gn-universe-ui";
import { Montserrat } from "next/font/google";
import localFont from "next/font/local";
import { camelizeKeys } from "humps";

import { AppConfigProvider } from "../contexts/appConfig";
import { AnalyticsProvider } from "../contexts/Analytics";
import * as OneTrust from "../contexts/OneTrust";
import UserProvider, { useUser } from "../contexts/user";
import { BeaconProvider } from "../contexts/Beacon";
import { TransmogrifierProvider } from "../contexts/Transmogrifier";
import SplashScreen from "../components/SplashScreen";
import {
  USER_TYPES,
  ROUTE_PREFIX,
  REGISTRATION_INITIATORS,
  OAUTH_INITIATORS,
} from "../constants";
import BugsnagClient from "../utils/bugsnag";
import config from "../config";
import { useProfile } from "../hooks";
import {
  registerComponent,
  registerFetcher,
} from "../components/LayoutRenderer";
import {
  CardList,
  Carousel,
  Spotlight,
  HeroVideo,
  HeroUnit,
} from "../components/LayoutRenderer/components";
import api from "../api";

import "../../../assets/styles/tailwind.css";
import "../../../assets/styles/vars.css";
import "../../../assets/styles/utils.css";

const ErrorBoundary = BugsnagClient.getPlugin("react").createErrorBoundary();
const segment = new AnalyticsBrowser();
const montserrat = Montserrat({
  subsets: ["latin"],
  variable: "--font-montserrat",
});
const degular = localFont({
  src: "../../../assets/fonts/degular.woff2",
  style: "normal",
  weight: "800",
  variable: "--font-degular",
});
const degularDisplay = localFont({
  src: "../../../assets/fonts/degular-display.woff2",
  style: "italic",
  weight: "800",
  variable: "--font-degular-display",
});

const client = api();

registerComponent("List", CardList);
registerComponent("spotlight", Spotlight);
registerComponent("HeroVideo", HeroVideo);
registerComponent("HeroUnit", HeroUnit);
registerComponent("HeroUnitCarousel", Carousel);
registerComponent("CustomShelf", CardList);

registerFetcher("shelves", ({ id }) => client.getShelf(id));
registerFetcher("shelves_default", ({ position }) =>
  client.getDefaultShelf(position),
);
registerFetcher("spotlight", ({ spotlightId }) =>
  client.spotlight(spotlightId),
);
registerFetcher("hero_video", ({ videoId }) => client.getHeroVideo(videoId));
registerFetcher("hero_video_default", () => client.getDefaultHeroVideo());
registerFetcher("hero_unit", ({ heroUnitId }) =>
  client.getHeroUnit(heroUnitId),
);
registerFetcher("heroUnitCarousel", ({ id }) => client.getHeroUnitCarousel(id));
registerFetcher("custom_shelf", ({ id }) => client.getCustomShelf(id));

export default function GoNoodle({ Component, pageProps }) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  /**
   * Adding font-sans Tailwind util to the main element to ensure that the font is applied to the entire app as a default.
   * The values for the font-body class are defined in the tailwind.config.js file under the fontFamily.sans property.
   */
  React.useEffect(() => {
    // Adding fonts to the document body to ensure that they can be used inside portals.
    document.body.classList.add(
      montserrat.variable,
      degular.variable,
      degularDisplay.variable,
      "font-body",
    );
  }, []);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={config.RECAPTCHA_ENTERPRISE_KEY}
      scriptProps={{
        async: true,
        useEnterprise: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <OneTrust.Provider>
            <AppConfigProvider>
              <UserProvider>
                <ErrorBoundary>
                  <BootstrapApplication />
                  <AnalyticsProvider>
                    <BootstrapClientAnalytics />
                    <SSRProvider>
                      <OverlayProvider>
                        <BeaconProvider>
                          <SplashScreen>
                            <Toast.Provider>
                              <TransmogrifierProvider>
                                <Component {...pageProps} />
                              </TransmogrifierProvider>
                            </Toast.Provider>
                          </SplashScreen>
                        </BeaconProvider>
                      </OverlayProvider>
                    </SSRProvider>
                  </AnalyticsProvider>
                </ErrorBoundary>
              </UserProvider>
            </AppConfigProvider>
          </OneTrust.Provider>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </GoogleReCaptchaProvider>
  );
}

const BootstrapClientAnalytics = () => {
  const router = useRouter();
  const { user, updateUser } = useUser();
  const performanceConsentStatus = OneTrust.useConsentStatus(
    OneTrust.PERFORMANCE_SDKS,
  );
  const prevPerformanceConsentStatus = usePrevious(performanceConsentStatus);

  React.useEffect(() => {
    if (performanceConsentStatus === OneTrust.CONSENT_GIVEN) {
      segment
        .load(
          {
            writeKey: process.env.NEXT_PUBLIC_ANALYTICS_API_KEY,
          },
          { initialPageview: true },
        )
        .catch((err) => BugsnagClient.notify(err));
    }
  }, [performanceConsentStatus]);

  React.useEffect(() => {
    if (
      prevPerformanceConsentStatus === OneTrust.CONSENT_GIVEN &&
      performanceConsentStatus === OneTrust.CONSENT_NOT_GIVEN
    ) {
      segment?.reset();
      window.location.reload();
    }
  }, [performanceConsentStatus, prevPerformanceConsentStatus]);

  React.useEffect(() => {
    const handleRouteChange = (_, { shallow }) => {
      if (shallow === false) {
        segment.page();
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  React.useEffect(() => {
    if (user?.analyticsId && user?.userTypeId) {
      segment.ready(() => {
        segment.identify(user?.analyticsId, {
          user_type: find(USER_TYPES, { id: user.userTypeId })?.name,
        });
      });
    }
  }, [user?.analyticsId, user?.userTypeId]);

  React.useEffect(() => {
    if (
      user?.allowTracking === false &&
      performanceConsentStatus === OneTrust.CONSENT_GIVEN
    ) {
      updateUser({ allowTracking: true });
    } else if (
      user?.allowTracking === true &&
      performanceConsentStatus === OneTrust.CONSENT_NOT_GIVEN
    ) {
      updateUser({ allowTracking: false });
    }
  }, [user?.allowTracking, updateUser, performanceConsentStatus]);

  return null;
};

function checkUrlAvailability(url, options = {}) {
  return new Promise((resolve, reject) => {
    fetch(url, { method: "HEAD", ...options })
      .then((response) => {
        if (
          response.ok ||
          (options.mode === "no-cors" && response.type === "opaque")
        ) {
          resolve(`${url} is responding.`);
        } else {
          reject(new Error(`${url} - Status error ${response.status}`));
        }
      })
      .catch((error) => {
        reject(new Error(`${url} - Access error: ${error}`));
      });
  });
}

const BootstrapApplication = () => {
  const router = useRouter();
  const { user } = useUser();
  const { hasSelectedProfile } = useProfile();
  const { oauthInitiator } = camelizeKeys(router.query);

  // Checks if the app can access the JWPlayer CDN main URLs and Google Analytics.
  React.useEffect(() => {
    Promise.allSettled([
      checkUrlAvailability("https://www.googletagmanager.com/gtag/js"),
      checkUrlAvailability("https://www.google-analytics.com/analytics.js", {
        mode: "no-cors",
      }),
      checkUrlAvailability(
        "https://ssl.p.jwpcdn.com/player/v/8.28.1/jwpsrv.js",
      ),
      checkUrlAvailability(
        "https://content.jwplatform.com/manifests/sx5o36op.m3u8",
      ),
      checkUrlAvailability("https://cdn.jwplayer.com/manifests/sx5o36op.m3u8"),
      checkUrlAvailability(
        "https://ssl.p.jwpcdn.com/player/v/8.12.5/jwplayer.js",
      ),
      checkUrlAvailability(
        "https://live-cdn.jwplayer.com/live/channels/4iZJbICN.json",
      ),
      checkUrlAvailability(
        "https://assets-jpcust.jwpsrv.com/thumbnails/him12igc-720.jpg",
      ),
      checkUrlAvailability("https://prd.jwpltx.com/v1/jwplayer6/ping.gif", {
        mode: "no-cors",
      }),
    ])
      .then((urlsCheckResults) => {
        let rejectedMessages = "";
        urlsCheckResults.forEach(({ status, reason }) => {
          if (!!reason && status === "rejected") {
            rejectedMessages += `${reason}\n `;
          }
        });
        if (rejectedMessages !== "") {
          BugsnagClient.notify({
            name: "URLs check errors",
            message: rejectedMessages,
          });
        }
      })
      .catch((error) => {
        BugsnagClient.notify(
          `Error while performing URLs check: ${error?.message}`,
        );
      });
  }, []);

  // Redirects user to the profiles page on every new browsing session.
  React.useEffect(() => {
    if (
      user?.isLoggedIn &&
      !hasSelectedProfile &&
      router.pathname !== `/${ROUTE_PREFIX.PROFILES}` &&
      router.query.registrationInitiator !== REGISTRATION_INITIATORS.CLEVER && // Routing to the profiles page is handled in the OAuth callback instead of here.
      [
        OAUTH_INITIATORS.CLEVER_SIGNUP,
        OAUTH_INITIATORS.CLEVER_LOGIN,
        OAUTH_INITIATORS.CLEVER_CONNECT,
        OAUTH_INITIATORS.CLEVER,
      ].includes(oauthInitiator) === false // Routing to the profiles page is handled in the OAuth callback instead of here.
    ) {
      router.push(
        `/${ROUTE_PREFIX.PROFILES}?intendedPath=${encodeUri(router.asPath)}`,
      );
    }
  }, [user, hasSelectedProfile, router, oauthInitiator]);

  return null;
};
